import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, CircularProgress, Alert, Box, Button,
  Divider, List, ListItem, ListItemButton, ListItemText, ListItemAvatar,
  Avatar, IconButton, Chip, useTheme, useMediaQuery, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useBreadcrumb } from '../components/BreadcrumbContext';
import { useShoppingMode } from '../components/ShoppingModeContext';
import { apiFetch } from '../apiFetch';
import { ShoppingList } from '../types/ShoppingList';
import { ShoppingListGrocery } from '../types/ShoppingListGrocery';
import { Price } from '../types/Price';
import { Store } from '../types/Store';
import ShoppingListFormModal from '../modals/ShoppingListFormModal';
import ShoppingListGroceryFormModal from '../modals/ShoppingListGroceryFormModal';
import DeleteModal from '../modals/DeleteModal';
import BulkAddGroceriesModal from '../modals/BulkAddGroceriesModal';
import AnalyzeListModal from '../modals/AnalyzeListModal';

interface LocationState {
  fromPrices?: {
    groceryId: number;
    groceryName: string;
  };
}

const ViewShoppingList: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get prices origin from location state
  const locationState = location.state as LocationState | null;
  const fromPrices = locationState?.fromPrices;
  const [shoppingList, setShoppingList] = useState<ShoppingList | null>(null);
  const [groceries, setGroceries] = useState<ShoppingListGrocery[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [groceriesLoading, setGroceriesLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { setOverrideLabel } = useBreadcrumb();
  const { shoppingMode, startShopping, endShopping } = useShoppingMode();

  // For calculating total (we need prices)
  const [prices, setPrices] = useState<Map<number, Price>>(new Map());

  // Modal state for shopping list
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [missingPricesAlertOpen, setMissingPricesAlertOpen] = useState(false);
  const [confirmFinishOpen, setConfirmFinishOpen] = useState(false);
  const [analyzeModalOpen, setAnalyzeModalOpen] = useState(false);

  // Duplicate state
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateConfirmOpen, setDuplicateConfirmOpen] = useState(false);

  // Modal state for groceries
  const [groceryFormModalOpen, setGroceryFormModalOpen] = useState(false);
  // const [editingGroceryItem, setEditingGroceryItem] = useState<ShoppingListGrocery | null>(null);
  const [editingGroceryItem, setEditingGroceryItem] = useState<ShoppingListGrocery | null>(null);
  const [bulkAddModalOpen, setBulkAddModalOpen] = useState(false);
  const [deleteGroceryModalOpen, setDeleteGroceryModalOpen] = useState(false);
  const [deletingGroceryItem, setDeletingGroceryItem] = useState<ShoppingListGrocery | null>(null);

  // Switch store modal state
  const [switchStoreModalOpen, setSwitchStoreModalOpen] = useState(false);
  const [stores, setStores] = useState<Store[]>([]);
  const [switchStoreSelectedStore, setSwitchStoreSelectedStore] = useState<Store | null>(null);
  const [switchStoreLoading, setSwitchStoreLoading] = useState(false);
  const [storesLoading, setStoresLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;

  // Fetch shopping list details
  useEffect(() => {
    const fetchShoppingList = async () => {
      try {
        const data = await apiFetch(`${apiUrl}/api/shopping-lists/${id}/`);
        setShoppingList(data);
        setOverrideLabel(data.name);
      } catch (err: any) {
        setError(err.message || 'An error occurred while fetching shopping list details.');
      } finally {
        setLoading(false);
      }
    };
    fetchShoppingList();
  }, [apiUrl, id, setOverrideLabel]);

  // Fetch groceries in the list
  useEffect(() => {
    const fetchGroceries = async () => {
      try {
        const data = await apiFetch(`${apiUrl}/api/shopping-lists/${id}/groceries/`);
        setGroceries(data);
      } catch (err: any) {
        setError(err.message || 'An error occurred while fetching groceries.');
      } finally {
        setGroceriesLoading(false);
      }
    };
    fetchGroceries();
  }, [apiUrl, id]);

  // Fetch prices for the assigned store (completed or active/paused lists)
  useEffect(() => {
    const fetchPrices = async () => {
      if (!shoppingList) return;

      // Determine which shop to fetch prices from
      let shopId: number | null = null;
      let asOfDate: string | null = null;

      if (shoppingList.completed && shoppingList.completed_at_shop) {
        // Completed list: use historical prices from completion time
        shopId = shoppingList.completed_at_shop;
        asOfDate = shoppingList.completed_at || null;
      } else if (!shoppingList.completed && shoppingList.shop) {
        // Active/paused list: use current prices from assigned shop
        shopId = shoppingList.shop;
      }

      if (!shopId) {
        setPrices(new Map());
        return;
      }

      try {
        let url = `${apiUrl}/api/shops/${shopId}/prices/`;
        if (asOfDate) {
          url += `?as_of=${encodeURIComponent(asOfDate)}`;
        }
        const data = await apiFetch(url);
        const priceMap = new Map<number, Price>();
        data.forEach((p: Price) => {
          priceMap.set(p.grocery, p);
        });
        setPrices(priceMap);
      } catch (err: any) {
        // Non-critical, just for totals
      }
    };
    fetchPrices();
  }, [apiUrl, shoppingList]);

  const handleEditSuccess = (updatedList: ShoppingList) => {
    setShoppingList(updatedList);
    setOverrideLabel(updatedList.name);
  };

  const handleDeleteConfirm = async () => {
    if (!shoppingList) return;
    try {
      await apiFetch(`${apiUrl}/api/shopping-lists/${shoppingList.id}/`, {
        method: 'DELETE',
      });
      navigate('/shopping-lists', { state: { message: 'Shopping list deleted successfully.' } });
    } catch (err: any) {
      setError(err.message || 'An error occurred while deleting the shopping list.');
      setDeleteModalOpen(false);
    }
  };

  // Handle complete shopping list - show confirmation first
  const handleFinishShopping = () => {
    if (!shoppingList || shoppingList.completed) return;

    // Check if all picked items have prices
    if (hasUnpricedPicked) {
      setMissingPricesAlertOpen(true);
      return;
    }

    // Show confirmation dialog
    setConfirmFinishOpen(true);
  };

  // Confirm finish shopping
  const handleConfirmFinish = async () => {
    if (!shoppingList) return;

    setConfirmFinishOpen(false);
    try {
      const updated = await apiFetch(`${apiUrl}/api/shopping-lists/${shoppingList.id}/toggle-complete/`, {
        method: 'POST',
      });
      setShoppingList(updated);
      // Clear shopping mode if this was the active list
      if (shoppingMode.listId === shoppingList.id) {
        endShopping();
      }
      setSuccessMessage('Shopping list completed!');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to complete list.');
    }
  };

  // Handle resume shopping for paused lists
  const handleResumeShopping = async () => {
    if (!shoppingList || shoppingList.status !== 'paused') return;

    try {
      // Activate the list
      const updated = await apiFetch(`${apiUrl}/api/shopping-lists/${shoppingList.id}/update-status/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'active' }),
      });

      // Update shopping mode context
      if (updated.shop && updated.shop_name) {
        startShopping(updated.shop, updated.shop_name, updated.id, updated.name);
      }

      // Navigate to Go Shopping page
      navigate('/going-shopping');
    } catch (err: any) {
      setError(err.message || 'Failed to resume shopping.');
    }
  };

  // Handle switch store
  const handleSwitchStoreClick = async () => {
    setSwitchStoreSelectedStore(null);
    setSwitchStoreModalOpen(true);
    if (stores.length === 0) {
      setStoresLoading(true);
      try {
        const data = await apiFetch(`${apiUrl}/api/shops/`);
        setStores(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load stores.');
      } finally {
        setStoresLoading(false);
      }
    }
  };

  const handleSwitchStoreConfirm = async () => {
    if (!shoppingList || !switchStoreSelectedStore) return;

    setSwitchStoreLoading(true);
    try {
      const response = await apiFetch(`${apiUrl}/api/shopping-lists/${shoppingList.id}/switch-store/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop_id: switchStoreSelectedStore.id }),
      });

      if (response.mode === 'simple_switch') {
        // Simple switch - just update the current list
        setShoppingList(response.list);
        // Update shopping mode context if this was the active list
        if (shoppingMode.listId === shoppingList.id) {
          startShopping(switchStoreSelectedStore.id, switchStoreSelectedStore.name, shoppingList.id, shoppingList.name);
        }
        setSuccessMessage(`Switched to ${switchStoreSelectedStore.name}`);
      } else if (response.mode === 'move_items') {
        // Items moved to new list - navigate to the new list
        setSuccessMessage(`${response.items_moved} item(s) moved to new list at ${switchStoreSelectedStore.name}`);
        // Update shopping mode context with the new list
        startShopping(switchStoreSelectedStore.id, switchStoreSelectedStore.name, response.new_list.id, response.new_list.name);
        // Navigate to the new list
        navigate(`/shopping-lists/${response.new_list.id}`);
      }

      setSwitchStoreModalOpen(false);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to switch store.');
    } finally {
      setSwitchStoreLoading(false);
    }
  };

  // Handle duplicate list
  const handleDuplicateClick = () => {
    setDuplicateConfirmOpen(true);
  };

  const handleDuplicateConfirm = async () => {
    if (!shoppingList) return;

    setDuplicateConfirmOpen(false);
    setDuplicating(true);
    setError(null);

    try {
      const newList: ShoppingList = await apiFetch(`${apiUrl}/api/shopping-lists/${shoppingList.id}/duplicate/`, {
        method: 'POST',
      });

      setDuplicating(false);
      // Navigate to the new list
      navigate(`/shopping-lists/${newList.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to duplicate shopping list.');
      setDuplicating(false);
    }
  };

  // Grocery item handlers
  const handleAddGroceryClick = () => {
    setBulkAddModalOpen(true);
  };

  const handleEditGroceryClick = (e: React.MouseEvent, item: ShoppingListGrocery) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingGroceryItem(item);
    setGroceryFormModalOpen(true);
  };

  const handleDeleteGroceryClick = (e: React.MouseEvent, item: ShoppingListGrocery) => {
    e.preventDefault();
    e.stopPropagation();
    setDeletingGroceryItem(item);
    setDeleteGroceryModalOpen(true);
  };

  const handleGroceryFormSuccess = (savedItem: ShoppingListGrocery) => {
    // This is now only used for editing quantity
    setGroceries((prev) =>
      prev.map((g) => (g.id === savedItem.id ? savedItem : g))
    );
    setSuccessMessage('Grocery updated successfully.');
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const handleBulkAddSuccess = (savedItems: ShoppingListGrocery[]) => {
    setGroceries((prev) =>
      [...prev, ...savedItems].sort((a, b) =>
        a.grocery_details.name.localeCompare(b.grocery_details.name)
      )
    );
    setSuccessMessage(`Added ${savedItems.length} item${savedItems.length !== 1 ? 's' : ''} to list.`);
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const handleDeleteGroceryConfirm = async () => {
    if (!deletingGroceryItem) return;
    try {
      await apiFetch(`${apiUrl}/api/shopping-lists/${id}/groceries/${deletingGroceryItem.id}/`, {
        method: 'DELETE',
      });
      setGroceries((prev) => prev.filter((g) => g.id !== deletingGroceryItem.id));
      setSuccessMessage('Grocery removed from list.');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError(err.message || 'An error occurred while removing the grocery.');
    } finally {
      setDeleteGroceryModalOpen(false);
      setDeletingGroceryItem(null);
    }
  };

  // Format grocery secondary text
  const formatGrocerySecondary = (item: ShoppingListGrocery): string => {
    const g = item.grocery_details;
    const parts = [];
    if (g.brand) parts.push(g.brand);
    if (g.unit) parts.push(g.unit);
    if (g.packaging_size) parts.push(g.packaging_size);
    return parts.join(' | ');
  };

  // Calculate total for completed list - memoized
  const total = useMemo(() => {
    let sum = 0;
    groceries.forEach((item) => {
      const price = prices.get(item.grocery);
      if (price) {
        sum += parseFloat(price.price) * item.picked;
      }
    });
    return sum;
  }, [groceries, prices]);

  // Get existing grocery IDs for filtering dropdown - memoized
  const existingGroceryIds = useMemo(() => groceries.map((g) => g.grocery), [groceries]);

  // Format completed date - memoized
  const formattedCompletedDate = useMemo(() => {
    if (!shoppingList?.completed_at) return '';
    const date = new Date(shoppingList.completed_at);
    const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
    const day = date.getDate();
    const suffix = ['th', 'st', 'nd', 'rd'][(day % 10 > 3 || Math.floor(day % 100 / 10) === 1) ? 0 : day % 10];
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    return `${dayOfWeek}, ${day}${suffix} ${month} ${year}`;
  }, [shoppingList?.completed_at]);

  // Check if all items are picked - memoized
  const allItemsPicked = useMemo(() => {
    if (groceries.length === 0) return false;
    return groceries.every((item) => item.picked > 0);
  }, [groceries]);

  // Calculate picked/unpicked counts for switch store and check for unpriced picked items
  const { itemsPickedCount, fullyUnpickedCount, hasUnpricedPicked } = useMemo(() => {
    let pickedCount = 0;
    let unpickedCount = 0;
    let unpricedPicked = false;
    groceries.forEach((item) => {
      if (item.picked > 0) {
        pickedCount++;
        // Check if this picked item has a price
        if (!prices.get(item.grocery)) {
          unpricedPicked = true;
        }
      } else {
        unpickedCount++;
      }
    });
    return { itemsPickedCount: pickedCount, fullyUnpickedCount: unpickedCount, hasUnpricedPicked: unpricedPicked };
  }, [groceries, prices]);

  // Determine if Switch Store button should be shown
  const showSwitchStoreButton = useMemo(() => {
    if (!shoppingList) return false;
    if (shoppingList.completed) return false;
    // Only show for active/paused lists that have unpicked items
    const isActiveOrPaused = shoppingList.status === 'active' || shoppingList.status === 'paused';
    return isActiveOrPaused && !allItemsPicked;
  }, [shoppingList, allItemsPicked]);

  // Determine if Finish button should be shown
  const showFinishButton = useMemo(() => {
    if (!shoppingList) return false;
    // Don't show for completed lists (no reopen option)
    if (shoppingList.completed) return false;
    // Show Finish only for active/paused lists with all items picked
    const isActiveOrPaused = shoppingList.status === 'active' || shoppingList.status === 'paused';
    return isActiveOrPaused && allItemsPicked;
  }, [shoppingList, allItemsPicked]);

  // Determine if Resume Shopping button should be shown
  const showResumeButton = useMemo(() => {
    if (!shoppingList) return false;
    // Show Resume only for paused lists
    return shoppingList.status === 'paused';
  }, [shoppingList]);

  if (loading) {
    return <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />;
  }
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  if (!shoppingList) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Alert severity="warning">Shopping list not found.</Alert>
      </Container>
    );
  }

  // Render a grocery list item - handles both deleted and active groceries
  const renderGroceryItem = (item: ShoppingListGrocery) => {
    const isDeleted = item.grocery_details.deleted;
    const price = prices.get(item.grocery);
    // Show price if we have one and a shop is assigned (completed or active/paused)
    const showPrice = price && (
      (shoppingList.completed && shoppingList.completed_at_shop) ||
      (!shoppingList.completed && shoppingList.shop)
    );

    const content = (
      <>
        <ListItemAvatar>
          <Avatar
            src={item.grocery_details.image_icon || `${process.env.REACT_APP_IMAGES_URL}/placeholder-grocery.svg`}
            variant="rounded"
            sx={{
              width: 40,
              height: 40,
              opacity: isDeleted ? 0.5 : 1,
            }}
          />
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                component="span"
                sx={{
                  textDecoration: isDeleted || (item.picked === item.quantity && item.quantity > 0) ? 'line-through' : 'none',
                  color: isDeleted ? 'text.secondary' : (item.picked === item.quantity && item.quantity > 0) ? 'text.secondary' : 'text.primary',
                }}
              >
                {`${item.quantity}x ${item.grocery_details.name}`}
              </Typography>
              {isDeleted && (
                <Chip
                  label="Deleted"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    bgcolor: 'action.disabledBackground',
                    color: 'text.secondary',
                  }}
                />
              )}
              {!isDeleted && item.picked > 0 && item.picked < item.quantity && (
                <Chip label={`${item.picked}/${item.quantity} picked`} size="small" color="default" />
              )}
              {!isDeleted && item.picked === item.quantity && item.quantity > 0 && (
                <Chip label="Picked" size="small" color="primary" />
              )}
            </Box>
          }
          secondary={
            <>
              {formatGrocerySecondary(item) && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                  {formatGrocerySecondary(item)}
                </Typography>
              )}
              {showPrice && (
                <Typography component="span" variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <strong>${parseFloat(price.price).toFixed(2)}</strong>
                  {item.quantity > 1 && (
                    <Typography component="span" variant="body2" color="text.secondary">
                      (${(parseFloat(price.price) * item.quantity).toFixed(2)} total)
                    </Typography>
                  )}
                  {price.is_discounted && (
                    <Chip
                      label="Sale"
                      size="small"
                      color="error"
                      sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }}
                    />
                  )}
                </Typography>
              )}
            </>
          }
          sx={{ ml: 1 }}
        />
        {!shoppingList.completed && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isDeleted && (
              <IconButton
                size="small"
                onClick={(e) => handleEditGroceryClick(e, item)}
                aria-label="edit grocery quantity"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={(e) => handleDeleteGroceryClick(e, item)}
              aria-label="remove grocery from list"
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </>
    );

    // If deleted, render as non-clickable ListItem
    if (isDeleted) {
      return (
        <ListItem
          key={item.id}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            opacity: 0.7,
            py: 1,
            px: 2,
          }}
        >
          {content}
        </ListItem>
      );
    }

    // If not deleted, render as clickable ListItemButton with link
    return (
      <ListItemButton
        key={item.id}
        component={RouterLink}
        to={`/groceries/${item.grocery}`}
        state={{ fromShoppingList: { id: shoppingList.id, name: shoppingList.name } }}
        sx={{ display: 'flex', justifyContent: 'space-between' }}
      >
        {content}
      </ListItemButton>
    );
  };

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      {/* Title row */}
      <Box className="title-row">
        <IconButton onClick={() => navigate('/shopping-lists')} aria-label="back to shopping lists">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4" component="h1" className="page-title">{shoppingList.name}</Typography>
          {(() => {
            if (shoppingList.status === 'completed' || shoppingList.completed) {
              return <Chip label="Done" size="small" color="primary" variant="filled" sx={{ fontSize: '0.7rem' }} />;
            }
            if (shoppingList.status === 'active') {
              return <Chip label="Active" size="small" color="success" variant="filled" sx={{ fontSize: '0.7rem' }} />;
            }
            if (shoppingList.status === 'paused') {
              return <Chip label="Paused" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.7rem' }} />;
            }
            return <Chip label="Open" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem' }} />;
          })()}
        </Box>
      </Box>
      {shoppingList.shop_name && !shoppingList.completed && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Shopping at: <strong>{shoppingList.shop_name}</strong>
        </Typography>
      )}
      {shoppingList.duplicated_from && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Copy of{' '}
          {shoppingList.duplicated_from_deleted ? (
            <em>{shoppingList.duplicated_from_name} (deleted)</em>
          ) : (
            <RouterLink
              to={`/shopping-lists/${shoppingList.duplicated_from}`}
              style={{ color: 'inherit' }}
            >
              {shoppingList.duplicated_from_name}
            </RouterLink>
          )}
        </Typography>
      )}

      {shoppingList.description && (
        <Typography variant="body1" gutterBottom sx={{ mt: 1 }}>
          {shoppingList.description}
        </Typography>
      )}

      {/* Back to prices link */}
      {fromPrices && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            component={RouterLink}
            to={`/prices/grocery/${fromPrices.groceryId}`}
            endIcon={<ArrowForwardIcon />}
            size="small"
            sx={{ whiteSpace: 'nowrap' }}
          >
            {fromPrices.groceryName} Prices
          </Button>
        </Box>
      )}

      {/* Action buttons */}
      <Box className="page-header-buttons" sx={{ mb: 2 }}>
        {shoppingList.completed && (
          isMobile ? (
            <Tooltip title="Duplicate this list" enterTouchDelay={500}>
              <IconButton
                color="primary"
                onClick={handleDuplicateClick}
                disabled={duplicating}
                sx={{ border: 1, borderColor: 'primary.main' }}
              >
                {duplicating ? <CircularProgress size={20} color="inherit" /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={duplicating ? <CircularProgress size={16} color="inherit" /> : <ContentCopyIcon />}
              onClick={handleDuplicateClick}
              disabled={duplicating}
            >
              Duplicate
            </Button>
          )
        )}
        {showSwitchStoreButton && (
          isMobile ? (
            <Tooltip title="Switch to a different store" enterTouchDelay={500}>
              <IconButton
                color="primary"
                onClick={handleSwitchStoreClick}
                sx={{ border: 1, borderColor: 'primary.main' }}
              >
                <SwapHorizIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SwapHorizIcon />}
              onClick={handleSwitchStoreClick}
            >
              Switch Store
            </Button>
          )
        )}
        {showResumeButton && (
          isMobile ? (
            <Tooltip title="Resume shopping" enterTouchDelay={500}>
              <IconButton
                color="success"
                onClick={handleResumeShopping}
                sx={{ border: 1, borderColor: 'success.main' }}
              >
                <PlayArrowIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              variant="contained"
              color="success"
              startIcon={<PlayArrowIcon />}
              onClick={handleResumeShopping}
            >
              Resume Shopping
            </Button>
          )
        )}
        {isMobile ? (
          <Tooltip title="Analyze price trends" enterTouchDelay={500}>
            <span>
              <IconButton
                color="info"
                onClick={() => setAnalyzeModalOpen(true)}
                disabled={groceriesLoading || groceries.length === 0}
                sx={{ border: 1, borderColor: 'info.main' }}
              >
                <AnalyticsIcon />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Button
            variant="outlined"
            color="info"
            startIcon={<AnalyticsIcon />}
            onClick={() => setAnalyzeModalOpen(true)}
            disabled={groceriesLoading || groceries.length === 0}
          >
            Analyze
          </Button>
        )}
        {showFinishButton && (
          isMobile ? (
            <Tooltip title="Mark as complete" enterTouchDelay={500}>
              <IconButton
                color="primary"
                onClick={handleFinishShopping}
                sx={{ border: 1, borderColor: 'primary.main' }}
              >
                <CheckCircleIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckCircleIcon />}
              onClick={handleFinishShopping}
            >
              Finish
            </Button>
          )
        )}
        {isMobile ? (
          <Tooltip title="Edit list details" enterTouchDelay={500}>
            <IconButton
              color="primary"
              onClick={() => setFormModalOpen(true)}
              sx={{ border: 1, borderColor: 'primary.main' }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<EditIcon />}
            onClick={() => setFormModalOpen(true)}
          >
            Edit
          </Button>
        )}
        {isMobile ? (
          <Tooltip title="Delete this list" enterTouchDelay={500}>
            <IconButton
              color="error"
              onClick={() => setDeleteModalOpen(true)}
              sx={{ border: 1, borderColor: 'error.main' }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteModalOpen(true)}
          >
            Delete
          </Button>
        )}
      </Box>
      {/* Show total for completed lists */}
      {shoppingList.completed && (
        <Box sx={{ p: 2, bgcolor: 'primary.main', borderRadius: 1, mb: 2 }}>
          <Typography
            variant={isMobile ? 'body2' : 'body1'}
            color="primary.contrastText"
          >
            Shopping completed: ${total.toFixed(2)}
            {shoppingList.completed_at_shop_name && (
              <> at {shoppingList.completed_at_shop_name}</>
            )}
            {formattedCompletedDate && (
              <> on {formattedCompletedDate}</>
            )}
          </Typography>
        </Box>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Groceries Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2">Groceries</Typography>
        {!shoppingList.completed && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddGroceryClick}
          >
            Add
          </Button>
        )}
      </Box>

      {groceriesLoading ? (
        <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />
      ) : groceries.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          No groceries in this list yet. Click "Add" to get started.
        </Typography>
      ) : (
        <List>
          {groceries.map((item) => renderGroceryItem(item))}
        </List>
      )}

      {/* Edit Shopping List Modal */}
      <ShoppingListFormModal
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        onSuccess={handleEditSuccess}
        shoppingList={shoppingList}
      />

      {/* Delete Shopping List Modal */}
      <DeleteModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={handleDeleteConfirm}
        itemName={shoppingList.name}
        warning={shoppingList.completed ? 'Deleting this completed list will remove it from your activity and statistics data.' : undefined}
      />

      {/* Missing Prices Alert */}
      <Dialog
        open={missingPricesAlertOpen}
        onClose={() => setMissingPricesAlertOpen(false)}
      >
        <DialogTitle>Missing Prices</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Some picked items don't have prices set. Please add prices for all picked items before completing the shopping list.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMissingPricesAlertOpen(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Finish Shopping Dialog */}
      <Dialog
        open={confirmFinishOpen}
        onClose={() => setConfirmFinishOpen(false)}
      >
        <DialogTitle>Finish Shopping?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to complete this shopping list? This action cannot be undone. If you need to continue shopping later, you'll need to duplicate the list to create a new one.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmFinishOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirmFinish} variant="contained" color="primary">
            Finish Shopping
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Grocery Quantity Modal (for editing only) */}
      <ShoppingListGroceryFormModal
        open={groceryFormModalOpen}
        onClose={() => {
          setGroceryFormModalOpen(false);
          setEditingGroceryItem(null);
        }}
        onSuccess={handleGroceryFormSuccess}
        shoppingListId={parseInt(id!, 10)}
        existingGroceryIds={existingGroceryIds}
        item={editingGroceryItem}
      />

      {/* Bulk Add Groceries Modal */}
      <BulkAddGroceriesModal
        open={bulkAddModalOpen}
        onClose={() => setBulkAddModalOpen(false)}
        onSuccess={handleBulkAddSuccess}
        shoppingListId={parseInt(id!, 10)}
        existingGroceryIds={existingGroceryIds}
      />

      {/* Delete Grocery Modal */}
      <DeleteModal
        open={deleteGroceryModalOpen}
        onClose={() => {
          setDeleteGroceryModalOpen(false);
          setDeletingGroceryItem(null);
        }}
        onDelete={handleDeleteGroceryConfirm}
        itemName={deletingGroceryItem?.grocery_details.name || ''}
      />

      {/* Analyze List Modal */}
      <AnalyzeListModal
        open={analyzeModalOpen}
        onClose={() => setAnalyzeModalOpen(false)}
        groceries={groceries}
        listName={shoppingList.name}
      />

      {/* Duplicate Confirmation Dialog */}
      <Dialog open={duplicateConfirmOpen} onClose={() => setDuplicateConfirmOpen(false)}>
        <DialogTitle>Duplicate Shopping List?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will create a new shopping list called "{shoppingList.name} (Copy)" with the same items.
            The new list will be marked as incomplete so you can use it for your next shopping trip.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateConfirmOpen(false)}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleDuplicateConfirm} variant="contained" color="primary">
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Switch Store Modal */}
      <Dialog
        open={switchStoreModalOpen}
        onClose={() => !switchStoreLoading && setSwitchStoreModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapHorizIcon />
            Switch Store
          </Box>
        </DialogTitle>
        <DialogContent>
          {itemsPickedCount === 0 ? (
            // Mode 1: No items picked - simple switch
            <DialogContentText sx={{ mb: 2 }}>
              Select a different store to continue shopping.
            </DialogContentText>
          ) : (
            // Mode 2: Some items picked - move to new list
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                You have already picked <strong>{itemsPickedCount}</strong> item(s) at <strong>{shoppingList.shop_name}</strong>.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                You can switch to another store. This will move the <strong>{fullyUnpickedCount}</strong> unpicked item(s) to a new shopping list at the new store where you can continue shopping.
              </Typography>
              <Typography variant="body2">
                Your existing list will be marked as complete and you can review it in the shopping lists section later.
              </Typography>
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
            Select new store:
          </Typography>
          {storesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {stores.filter(s => s.id !== shoppingList.shop).map((store) => (
                <ListItemButton
                  key={store.id}
                  selected={switchStoreSelectedStore?.id === store.id}
                  onClick={() => setSwitchStoreSelectedStore(store)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    border: '1px solid',
                    borderColor: switchStoreSelectedStore?.id === store.id ? 'primary.main' : 'divider',
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={store.icon_url || `${process.env.REACT_APP_IMAGES_URL}/placeholder-store.svg`}
                      variant="rounded"
                      sx={{ width: 40, height: 40 }}
                    />
                  </ListItemAvatar>
                  <ListItemText primary={store.name} secondary={store.address_line1} />
                  {switchStoreSelectedStore?.id === store.id && (
                    <StorefrontIcon color="primary" />
                  )}
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSwitchStoreModalOpen(false)}
            disabled={switchStoreLoading}
          >
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleSwitchStoreConfirm}
            variant="contained"
            color="primary"
            disabled={!switchStoreSelectedStore || switchStoreLoading}
            startIcon={switchStoreLoading ? <CircularProgress size={16} /> : <SwapHorizIcon />}
          >
            {itemsPickedCount === 0 ? 'Switch Store' : 'Move To New List'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ViewShoppingList;
